import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
} from "react";
import { useDispatch } from "react-redux";
import { GetRunningTimerMutation } from "./mutations/GetRunningTimerMutation";
import { PositionEnum, Timer } from "./types";
import { CreateTimerMutation } from "./mutations/CreateTimerMutation";
import { GetAllTimersMutation } from "./mutations/GetAllTimersMutation";

import useConfig from "../../core/useConfig";
import useAlert from "../../core/useAlert";
import { dateStringBasedOnTimeZone, dateStringForFromAndToStringFormat } from "./helper";
import { timerActions } from "../../store/timerSlice";
import { setCookie, useDidMountEffect, useMyState } from "../../utils";

import { useEmail } from "../../core/MailLoader";
import { useAppSelector } from "../../store/hooks";
import { LoadingButton } from "@mui/lab";

interface HandleLocalValues {
  key?: string;
  value?: any;
}

export interface PausedTimer {
  id?: string;
  durationInSeconds: number | null;
  isPaused: boolean;
  event?: any;
}
export type TimerLoadingState = {
  startLoading: boolean;
  pauseLoading: boolean;
  currentTimerLoading: boolean;
  allTimersLoading: boolean;
};
interface TimerContextType {
  user: string | null;
  setUser: (user: string | null) => void;
  startTimer: (props?: TimerStartPauseResume) => void;
  pauseTimer: (props?: TimerStartPauseResume) => void;
  elapsedRef: any;
  elapsed?: number;
  isTimerRunning?: boolean;
  isGlobalTimerRunning?: boolean;
  loading?: TimerLoadingState;
  setLoading?: unknown;
  refetch?: boolean;
  localValues?: any;
  handleLocalValues?: ({ key, value }: HandleLocalValues) => void;
  pausedTimer?: PausedTimer;
  handleResetPausedTimer?: () => void;
  handleSetPausedTimer?: (props: PausedTimer) => void;
  timerMode?: number;
}

interface TimerStartPauseResume {
  id?: string | null | undefined;
  durationInSeconds?: number | null;
  timerStatus?: number;
  isTimerRunning?: boolean;
  callbacks?: { loadingHandler?: any; isRunningTimerHandler?: any; timerModeHandler?: any };
  isRunning?: boolean | null | undefined;
  isMatter?: boolean;
}

// Create the context with a default value
const TimerContext = createContext<TimerContextType | undefined>(undefined);

// Create a provider component
interface TimerProviderProps {
  children: ReactNode;
}

export const TimerProvider: React.FC<TimerProviderProps> = ({ children }) => {
  const [user, setUser] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null); // To hold the interval ID
  const startTimeRef = useRef<number | null>(null); // To store the start time
  const elapsedRef = useRef(0); // Initialize with the provided durationInSeconds
  const [elapsed, setElapsed] = useState(0); // Initialize with the provided durationInSeconds
  const [lcIsRunning, setLcIsRunning] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useMyState<TimerLoadingState>({
    startLoading: false,
    pauseLoading: false,
    currentTimerLoading: true,
    allTimersLoading: true,
  });
  const [refetch, setRefetch] = useState<boolean>(false);
  const [lcValues, setLcValues] = useState<any>({});
  const [pausedTimer, setPausedTimer] = useState<PausedTimer>({
    id: "",
    durationInSeconds: null,
    isPaused: false,
  });
  const { timezone } = useConfig();
  const {createEmailLogLoading} = useAppSelector(state=>state.timer)
  const [timerMode] = useState<number>(0);
  const { mailId } = useEmail();
  const showAlert = useAlert();

  const handleRefetch = () => {
    setRefetch((prev) => !prev);
  };

  const handleLocalValues = ({ key, value }: HandleLocalValues) => {
    setLcValues((prev: any) => {
      return { ...prev, [key as string]: value };
    });
  };

  const handleResetPausedTimer = () => {
    setPausedTimer({ id: "", durationInSeconds: null, isPaused: false });
  };

  // API CALLS

  const handleStartPauseResume = ({
    id,
    durationInSeconds,
    timerStatus,
    callbacks,
  }: TimerStartPauseResume) => {
    setLoading("startLoading", true);
    const payload = {
      durationInSeconds: durationInSeconds ? durationInSeconds : 0,
      id: id ? id : "",
      timerStatus: timerStatus ? timerStatus : 0,
      resourceId: mailId,
    };

    // To Reset the dates.

    const currentDateBasedOnTimeZone = dateStringBasedOnTimeZone(timezone!);
    const modifiedDates = dateStringForFromAndToStringFormat(currentDateBasedOnTimeZone!, timezone!);

    dispatch(
      CreateTimerMutation<Timer>(payload, {
        onSuccess: () => {
          handleLocalValues({ key: "fromDate", value: modifiedDates?.fromDate });
          handleLocalValues({ key: "toDate", value: modifiedDates?.toDate });
          callbacks?.loadingHandler(false);
          if (callbacks && callbacks.isRunningTimerHandler) {
            callbacks?.isRunningTimerHandler(true);
          }
          handleRefetch();
        },
        onLoading(loading: boolean) {
          setLoading("startLoading", loading);
          callbacks?.loadingHandler(false);
        },
      })
    );
  };

  const handlePauseResume = ({
    id,
    durationInSeconds,
    timerStatus,
    callbacks,
    isMatter,
  }: TimerStartPauseResume) => {
    setLoading("pauseLoading", true);
    const payload = {
      durationInSeconds: durationInSeconds ? durationInSeconds : 0,
      id: id ? id : "",
      timerStatus: timerStatus ? timerStatus : 0,
    };

    dispatch(
      CreateTimerMutation<Timer>(payload, {
        onSuccess: () => {
          callbacks?.loadingHandler(false);
          callbacks?.isRunningTimerHandler(false);
          handleRefetch();
          setLcIsRunning(false);
          if (!isMatter) {
            setPausedTimer({ durationInSeconds: durationInSeconds!, id: id!, isPaused: true });
          }
          dispatch(timerActions.getRecord({}));
          setElapsed(0);
          elapsedRef.current = 0;
        },
        onLoading(loading: boolean) {
          setLoading("pauseLoading", loading);
          callbacks?.loadingHandler(false);
        },
        onError: (message) => {
          showAlert(message, { variant: "info" });
        },
      })
    );
  };

  // Function to update the timer display
  const updateDisplay = () => {
    const now = Math.floor(Date.now() / 1000); // Get current timestamp in seconds
    const elapsed = elapsedRef.current + (startTimeRef.current ? now - startTimeRef.current : 0);
    setElapsed(elapsed);
  };

  // Function to start the timer
  const startTimer = (props?: TimerStartPauseResume) => {
    setPausedTimer({ durationInSeconds: null, id: "", isPaused: false });

    if (!lcIsRunning || !props?.isRunning) {
      setLcIsRunning(true); // Mark timer as running
      setCookie("isTimerRunning", "true");

      if (props?.durationInSeconds) {
        elapsedRef.current = props?.durationInSeconds;
      }
      startTimeRef.current = Math.floor(Date.now() / 1000); // Record the start time
      timerRef.current = window.setInterval(updateDisplay, 1000); // Update every second

      if (!props?.isTimerRunning) {
        if (props?.id) {
          handleStartPauseResume({ ...props, timerStatus: 2 });
        } else {
          handleStartPauseResume({ ...props, timerStatus: 0 });
        }
      }
    }
  };

  // Function to pause the timer
  const pauseTimer = (props?: TimerStartPauseResume) => {
    if (lcIsRunning) {
      setLcIsRunning(false); // Mark timer as paused
      setCookie("isTimerRunning", "false");
      clearInterval(timerRef.current as number); // Stop the interval
      const now = Math.floor(Date.now() / 1000);
      const lcElapsed = startTimeRef.current ? now - startTimeRef.current : 0; // Update elapsed time
      elapsedRef.current += lcElapsed;
      setElapsed(lcElapsed);
      startTimeRef.current = null; // Clear the start time
      // API CALL
      handlePauseResume({ ...props, timerStatus: 1 });
    }
  };

  const onSuccess = (data: any) => {
    elapsedRef.current = data?.durationInSeconds!;
    if (data !== null) {
      startTimer({ isTimerRunning: data?.isTimerRunning });
      const currentDateBasedOnTimeZone = dateStringBasedOnTimeZone(timezone!);
      const modifiedDates = dateStringForFromAndToStringFormat(
        currentDateBasedOnTimeZone!,
        timezone!
      );
      // call on when timer is started
      dispatch(
        timerActions.getSteps({
          step: {
            stepId: PositionEnum.TimeEntryCreate,
            title: "Time Entry Created",
            subTitle: "Time Entry Created",
            component: (callback) => (
              <LoadingButton loading={createEmailLogLoading} size="small" variant="contained" color={"primary"} onClick={callback}>
                Create Email Log
              </LoadingButton>
            ),
            data,
          },
          position: PositionEnum.TimeEntryCreate,
        })
      );
    }
    dispatch(
      GetAllTimersMutation<Timer[]>(
        {
          resourceId: mailId,
        },
        {
          onSuccess: (response) => {
            const isRunning = response?.every((r) => r.isTimerRunning);
            if (!isRunning) {
              const data = response?.find((r) => !r?.isTimerRunning);
              // call on when timer is stopped
              dispatch(
                timerActions.getSteps({
                  step: {
                    stepId: PositionEnum.TimeEntryCreate,
                    title: "Time Entry Created",
                    subTitle: "Time Entry Created",
                    component: (callback) => (
                      <LoadingButton loading={createEmailLogLoading} size="small" variant="contained" color={"primary"} onClick={callback}>
                        Create Email log
                      </LoadingButton>
                    ),
                    data,
                  },
                  position: PositionEnum.TimeEntryCreate,
                })
              );
            }
          },
          onLoading(loading) {
            setLoading("allTimersLoading", loading);
          },
        }
      )
    );
  };

  const handleSetPausedTimer = ({ id, durationInSeconds, isPaused }: PausedTimer) => {
    setPausedTimer({ id, durationInSeconds, isPaused });
  };

  useDidMountEffect(() => {
    dispatch(
      GetRunningTimerMutation<Timer>(mailId, {
        onSuccess,
        onLoading: (loading) => setLoading("currentTimerLoading", loading),
      })
    );

    return () => {
      clearInterval(timerRef.current as number);
    };
  }, [refetch, mailId]);

  return (
    <>
      <TimerContext.Provider
        value={{
          user,
          setUser,
          startTimer,
          pauseTimer,
          elapsedRef,
          elapsed,
          isGlobalTimerRunning: lcIsRunning,
          refetch,
          loading,
          setLoading,
          localValues: lcValues,
          handleLocalValues,
          pausedTimer,
          handleResetPausedTimer,
          handleSetPausedTimer,
          timerMode,
        }}
      >
        {children}
      </TimerContext.Provider>
    </>
  );
};

// Create a custom hook for consuming the context
export const useTimerContext = (): TimerContextType => {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
